import { Box, TableRow, TableCell, IconButton, Checkbox, Typography, Avatar } from '@mui/material';
import { Globe, Mail } from 'lucide-react';
import LinkedInLogo from '../../../../components/icons/LinkedIn';

import { memo } from 'react';
import { ProspectDto } from '@zaplify/prospects';
import { stringToColor } from '../../../../helpers/utils';

export const ContactRow = memo(
    ({
        prospect,
        selected,
        handleRowClick,
        handleCheckboxClick,
        labelId,
    }: {
        prospect: ProspectDto;
        selected: boolean;
        handleRowClick: (e: any, prospect: ProspectDto) => void;
        handleCheckboxClick: (e: any, prospect: ProspectDto) => void;
        labelId: string;
    }) => {
        const prospectData = prospect?.data;
        const prospectId = prospect?.id;

        if (!prospectId || !prospectData) {
            return null;
        }

        return (
            <TableRow
                hover
                key={prospectId}
                role="checkbox"
                aria-checked={selected}
                tabIndex={-1}
                selected={selected}
                onClick={(e) => handleRowClick(e, prospect)}
                sx={{
                    display: 'table-row',
                    padding:
                        'var(--no-spacing, 0px) var(--M-m-compact, 16px) var(--no-spacing, 0px) var(--S-s-regular, 8px)',
                    alignItems: 'center',
                    gap: '8px',
                    alignSelf: 'stretch',
                    border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                    background: '#FFF',
                    cursor: 'pointer',
                    '&:first-of-type': {
                        borderRadius:
                            'var(--radius-xl, 14px) var(--radius-xl, 14px) var(--radius-none, 0px) var(--radius-none, 0px)',
                    },
                    '&.Mui-selected': {
                        backgroundColor: '#FFF',
                    },
                    '&.MuiTableRow-hover:hover': {
                        backgroundColor: '#F7F8FA',
                        opacity: 1,
                    },
                }}
            >
                <TableCell sx={{ borderBottom: '0' }}>
                    <IconButton
                        sx={{
                            display: 'flex',
                            width: '40px',
                            height: '40px',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                        onClick={(e) => handleCheckboxClick(e, prospect)}
                    >
                        <Checkbox
                            color="primary"
                            checked={selected}
                            inputProps={{
                                'aria-labelledby': labelId,
                            }}
                            sx={{
                                '& .MuiSvgIcon-root': {
                                    fontSize: 24,
                                },
                            }}
                        />
                    </IconButton>
                </TableCell>
                <TableCell
                    id={labelId}
                    scope="row"
                    sx={{
                        whiteSpace: 'nowrap',
                        minWidth: '250px',
                        flex: '1 0 0',
                        color: '#3A4B59',
                        fontSize: 15,
                        fontWeight: 500,
                        borderBottom: '0',
                        paddingLeft: '0',
                        paddingRight: '24px',
                    }}
                >
                    <ContactCell fullName={prospectData.fullName} />
                </TableCell>
                <TableCell
                    align="left"
                    sx={{
                        flex: '1 0 0',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        WebkitBoxOrient: 'vertical',
                        borderBottom: '0',
                        minWidth: '180px',
                        paddingLeft: '0',
                        paddingRight: '24px',
                    }}
                >
                    <Typography
                        sx={{
                            color: 'var(--grey-palette-grey-6, #3A4B59)',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '21px /* 150% */',
                        }}
                    >
                        {prospectData.occupationTitle}
                    </Typography>
                </TableCell>
                <TableCell
                    sx={{ flex: '1 0 0', borderBottom: '0', minWidth: '230px', paddingLeft: '0', paddingRight: '8px' }}
                    align="left"
                >
                    <CompanyInfoCell
                        companyName={prospectData.organizationName}
                        companyDomain={prospectData.organizationDomain}
                    />
                </TableCell>
                <TableCell
                    sx={{ flex: '1 0 0', borderBottom: '0', minWidth: '120px', paddingLeft: '0', paddingRight: '8px' }}
                    align="left"
                >
                    {prospectData.industry}
                </TableCell>
                <TableCell
                    sx={{
                        flex: '1 0 0',
                        borderBottom: '0',
                        paddingLeft: '0',
                        paddingRight: '24px',
                    }}
                    align="center"
                >
                    {prospectData.organizationSize}
                </TableCell>
                <TableCell
                    sx={{
                        flex: '1 0 0',
                        borderBottom: '0',
                        whiteSpace: 'normal',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        paddingLeft: '0',
                        paddingRight: '24px',
                    }}
                    align="left"
                >
                    {prospectData.location}
                </TableCell>
            </TableRow>
        );
    }
);

const CompanyInfoCell = ({ companyName, companyDomain }: { companyName: string; companyDomain: string }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                height: '56px',
                alignItems: 'center',
                gap: 'var(--S-s-spacious, 12px)',
                flex: '1 0 0',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flex: '1 0 0',
                }}
            >
                <img
                    className="image"
                    style={{
                        display: 'flex',
                        width: '32px',
                        height: 'auto',

                        paddingTop: '8px',
                        paddingBottom: '8px',
                        gap: '8px',
                        borderRadius: 'var(--radius-round, 100px)',
                    }}
                    onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/images/no-logo-icon.svg';
                    }}
                    src={companyDomain ? `https://logo.clearbit.com/${companyDomain}` : '/images/no-logo-icon.svg'}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        gap: 'var(--S-3xs, 2px)',
                        flex: '1 0 0',
                    }}
                >
                    <Typography
                        sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            alignSelf: 'stretch',
                            overflow: 'hidden',
                            color: 'var(--grey-palette-grey-6, #3A4B59)',
                            textOverflow: 'ellipsis',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            lineHeight: '21px /* 150% */',
                            maxWidth: 200,
                        }}
                    >
                        {companyName}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}
                    >
                        <Typography
                            sx={{
                                whiteSpace: 'nowrap',
                                color: '#6583EF',
                                fontSize: 14,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 200,
                                '& > a': {
                                    textDecoration: 'none',
                                    color: '#6583EF',
                                    '& > a:hover': {
                                        textDecoration: 'underline',
                                    },
                                },
                            }}
                        >
                            <a href={'http://' + companyDomain} target="_blank">
                                <Globe
                                    size={12}
                                    style={{
                                        flexShrink: 0,
                                        marginRight: 'var(--S-2xs, 4px)',
                                    }}
                                />

                                {companyDomain}
                            </a>
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const ContactCell = ({ fullName }: { fullName: string }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--S-s-spacious, 12px)',
                flex: '1 0 0',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flex: '1 0 0',
                }}
            >
                <Avatar
                    sx={{
                        width: '32px',
                        height: '32px',
                        fontSize: '16px',
                        bgcolor: stringToColor(fullName),
                    }}
                >
                    {fullName.split(' ')[0][0]}
                    {fullName.split(' ')[1][0]}
                </Avatar>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        gap: 'var(--no-spacing, 0px)',
                        flex: '1 0 0',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                    >
                        <Typography
                            sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                                overflow: 'hidden',
                                color: 'var(--grey-palette-grey-6, #3A4B59)',
                                textOverflow: 'ellipsis',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                lineHeight: '21px /* 150% */',
                            }}
                        >
                            {fullName.slice(0, 40)}
                            {fullName.length > 40 ? '...' : ''}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                    }}
                >
                    <LinkedInLogo style={{ height: '16px', width: '16px' }} />
                    <Mail size={25} strokeWidth={1} fill="#969FAC" stroke="#fff" />
                </Box>
            </Box>
        </Box>
    );
};
